import {
  applyBinop, ema, prev, min, max, nanToZero, abs, hhv, llv, sma, linRegSlope,
} from './builtins'

const builtins = {
  ema, prev, min, max, nanToZero, abs, hhv, llv, sma, linRegSlope,
}

function tokenize(source) {
  const tokens = []
  let i = 0
  while (i < source.length) {
    if (/\s/.test(source[i])) {
      i += 1
    } else if (/[a-zA-Z_]/.test(source[i])) {
      let j = i
      while (j < source.length && /\w/.test(source[j])) j += 1
      tokens.push({ type: 'ident', value: source.slice(i, j) })
      i = j
    } else if (/[0-9]/.test(source[i])) {
      let j = i
      while (j < source.length && /[0-9.]/.test(source[j])) j += 1
      tokens.push({ type: 'number', value: parseFloat(source.slice(i, j)) })
      i = j
    } else if ('+-*/=(),'.includes(source[i])) {
      tokens.push({ type: 'punct', value: source[i] })
      i += 1
    } else {
      i += 1
    }
  }

  return tokens
}

class Parser {
  constructor(tokens) {
    this.tokens = tokens
    this.pos = 0
  }

  peek() { return this.tokens[this.pos] }

  consume() {
    const tok = this.tokens[this.pos]
    this.pos += 1

    return tok
  }

  expect(val) {
    const tok = this.consume()
    if (tok?.value !== val) throw new Error(`Expected '${val}', got '${tok?.value}'`)
  }

  parseExpr() { return this.parseAddSub() }

  parseAddSub() {
    let left = this.parseMulDiv()
    while (this.peek()?.value === '+' || this.peek()?.value === '-') {
      const op = this.consume().value
      left = {
        type: 'binop', op, left, right: this.parseMulDiv(),
      }
    }

    return left
  }

  parseMulDiv() {
    let left = this.parsePrimary()
    while (this.peek()?.value === '*' || this.peek()?.value === '/') {
      const op = this.consume().value
      left = {
        type: 'binop', op, left, right: this.parsePrimary(),
      }
    }

    return left
  }

  parsePrimary() {
    const tok = this.peek()
    if (!tok) throw new Error('Unexpected end of expression')

    if (tok.type === 'number') {
      this.consume()

      return { type: 'literal', value: tok.value }
    }

    if (tok.value === '(') {
      this.consume()
      const expr = this.parseExpr()
      this.expect(')')

      return expr
    }

    if (tok.type === 'ident') {
      this.consume()
      if (this.peek()?.value === '(') {
        this.consume()
        const args = []
        while (this.peek()?.value !== ')') {
          args.push(this.parseExpr())
          if (this.peek()?.value === ',') this.consume()
        }
        this.consume()

        return { type: 'call', name: tok.value, args }
      }

      return { type: 'ident', name: tok.value }
    }

    throw new Error(`Unexpected token: '${tok.value}'`)
  }
}

function parseLine(line) {
  const eqIdx = line.indexOf('=')
  if (eqIdx === -1) throw new Error(`Missing '=' in: ${line}`)
  const name = line.slice(0, eqIdx).trim()
  const tokens = tokenize(line.slice(eqIdx + 1))

  return { name, ast: new Parser(tokens).parseExpr() }
}

function evalNode(node, ctx) {
  switch (node.type) {
    case 'literal':
      return node.value

    case 'ident':
      if (!(node.name in ctx)) throw new Error(`Unknown variable: '${node.name}'`)

      return ctx[node.name]

    case 'binop':
      return applyBinop(node.op, evalNode(node.left, ctx), evalNode(node.right, ctx))

    case 'call': {
      const fn = builtins[node.name]
      if (!fn) throw new Error(`Unknown function: '${node.name}'`)

      return fn(...node.args.map(a => evalNode(a, ctx)))
    }

    default:
      throw new Error(`Unknown node type: ${node.type}`)
  }
}

export function evaluate(program, inputs) {
  const ctx = { ...inputs }
  const lines = program
    .split('\n')
    .map(l => l.trim())
    .filter(l => l && !l.startsWith('//'))

  lines.forEach(line => {
    const { name, ast } = parseLine(line)
    ctx[name] = evalNode(ast, ctx)
  })

  return ctx
}
